import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { associateWorkout, completeWorkout, getTrainingCalendar, listActivities, listTrainingPlans, skipWorkout } from "../api/endpoints";
import { Button, Card, EmptyState, ErrorMessage, Input, PageHeading, Select, Spinner } from "../components/ui";
import { useAsync } from "../hooks/useAsync";

interface TrainingPageData {
  plans: Awaited<ReturnType<typeof listTrainingPlans>>["plans"];
  calendar: Awaited<ReturnType<typeof getTrainingCalendar>>["workouts"];
  recentActivities: Awaited<ReturnType<typeof listActivities>>["activities"];
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function defaultTo(): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + 14);
  return date.toISOString().slice(0, 10);
}

export default function TrainingPage() {
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(defaultTo());
  const [selectedActivities, setSelectedActivities] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, error, loading, reload } = useAsync(
    async () => {
      const [plans, calendar, activities] = await Promise.all([
        listTrainingPlans(),
        getTrainingCalendar(from, to),
        listActivities({ page: 1 }),
      ]);
      return { plans: plans.plans, calendar: calendar.workouts, recentActivities: activities.activities } satisfies TrainingPageData;
    },
    [from, to],
  );

  const groupedWorkouts = useMemo(() => {
    const groups = new Map<string, TrainingPageData["calendar"]>();
    for (const workout of data?.calendar ?? []) {
      const key = workout.scheduledDate ?? "Unscheduled";
      groups.set(key, [...(groups.get(key) ?? []), workout]);
    }
    return [...groups.entries()];
  }, [data]);

  async function mutate(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error || !data) return <ErrorMessage error={error ?? new Error("Failed to load training plans")} onRetry={reload} />;

  return (
    <div className="space-y-6" data-testid="training-page">
      <PageHeading title="Training plans" subtitle="Plan detail is read-only here; completion actions stay available on the calendar." />
      {actionError && <ErrorMessage error={new Error(actionError)} />}

      <Card className="p-4" data-testid="training-range-panel">
        <div className="grid gap-4 md:grid-cols-3">
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>From</span>
            <Input type="date" value={from} onChange={(event) => setFrom(event.target.value)} data-testid="training-from-input" />
          </label>
          <label className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
            <span>To</span>
            <Input type="date" value={to} onChange={(event) => setTo(event.target.value)} data-testid="training-to-input" />
          </label>
          <div className="flex items-end">
            <Button variant="secondary" onClick={reload} data-testid="training-refresh-button">Refresh</Button>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Plans</h2>
        {data.plans.length === 0 ? (
          <div className="mt-4">
            <EmptyState title="No plans yet" description="Plans are managed through Hillory's planning tools, then shown here." testId="training-plans-empty-state" />
          </div>
        ) : (
          <div className="mt-4 grid gap-4 lg:grid-cols-2" data-testid="training-plan-list-panel">
            {data.plans.map((plan) => (
              <Link key={plan.id} to={`/training/plans/${plan.id}`} className="rounded-lg border border-gray-200 p-4 dark:border-[#2e2e2e]" data-testid="training-plan-detail-link">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{plan.name}</h3>
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">{plan.status}</span>
                </div>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Race: {plan.raceName ?? "Not set"}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{plan.daysUntilRace != null ? `${plan.daysUntilRace} days to race` : "No target race"}</p>
              </Link>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="text-heading-md text-gray-900 dark:text-gray-100">Calendar actions</h2>
        {groupedWorkouts.length === 0 ? (
          <div className="mt-4">
            <EmptyState title="No workouts in range" description="Widen the date range or add planned workouts through the planning tools." testId="training-calendar-empty-state" />
          </div>
        ) : (
          <div className="mt-4 space-y-4" data-testid="training-calendar-panel">
            {groupedWorkouts.map(([date, workouts]) => (
              <div key={date} className="space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{date}</h3>
                {workouts.map((workout) => (
                  <Card key={workout.id} className="p-4">
                    <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{workout.name}</h4>
                          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700 dark:bg-[#252525] dark:text-gray-200">{workout.workoutType}</span>
                          <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300">{workout.status}</span>
                        </div>
                        {workout.description && <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{workout.description}</p>}
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Target: {workout.targetDistanceKm != null ? `${workout.targetDistanceKm} km` : workout.targetDurationMinutes != null ? `${workout.targetDurationMinutes} min` : "Open"}</p>
                      </div>
                      <div className="space-y-2 xl:w-80">
                        <div className="flex gap-2">
                          <Button variant="secondary" disabled={busy !== null} onClick={() => void mutate(`complete-${workout.id}`, () => completeWorkout(workout.id))} data-testid="training-workout-complete-button">Complete</Button>
                          <Button variant="secondary" disabled={busy !== null} onClick={() => void mutate(`skip-${workout.id}`, () => skipWorkout(workout.id))} data-testid="training-workout-skip-button">Skip</Button>
                        </div>
                        <div className="flex gap-2">
                          <Select value={selectedActivities[workout.id] ?? ""} onChange={(event) => setSelectedActivities((current) => ({ ...current, [workout.id]: event.target.value }))} data-testid="training-workout-activity-select">
                            <option value="">Associate recent activity</option>
                            {data.recentActivities.map((activity) => (
                              <option key={activity.id} value={activity.id}>{activity.activityDate} · {activity.activityName}</option>
                            ))}
                          </Select>
                          <Button disabled={!selectedActivities[workout.id] || busy !== null} onClick={() => void mutate(`associate-${workout.id}`, () => associateWorkout(workout.id, selectedActivities[workout.id]!))} data-testid="training-workout-associate-button">Link</Button>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
